<script lang="ts" setup>
import { CircleClose } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { nextTick, onMounted, onUnmounted, ref, toRefs, watch } from "vue";
import { MapProps, TiandituGlobal } from "./map";
import markerIcon from "./marker.png";

defineOptions({
  name: "IMap",
});

// ==================== Props & Emits ====================
const props = defineProps(MapProps);

const emits = defineEmits([
  "update:modelValue",
  "change",
  "update:addressCode",
  "update:address",
]);

const { modelValue, disabled, options } = toRefs(props);

// ==================== 天地图 Token ====================
const TIANDITU_TOKEN = "ab0296cad21f656152228912c15abdfb";

// ==================== 模块级：加载天地图 JS API ====================
const _win = window as unknown as TiandituGlobal;

function loadTiandituScript(): Promise<void> {
  if (_win.__tdt_loaded) return Promise.resolve();
  if (_win.__tdt_loading) return _win.__tdt_loading;

  _win.__tdt_loading = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://api.tianditu.gov.cn/api?v=4.0&tk=${TIANDITU_TOKEN}`;
    script.onload = () => {
      _win.__tdt_loaded = true;
      _win.__tdt_loading = null;
      resolve();
    };
    script.onerror = () => {
      _win.__tdt_loading = null;
      reject(new Error("天地图 API 加载失败"));
    };
    document.head.appendChild(script);
  });
  return _win.__tdt_loading;
}

function getT(): any {
  return _win.T;
}

// ==================== 天地图 REST API 封装 ====================
async function tdtSearch(keyword: string): Promise<any[]> {
  const postStr = JSON.stringify({
    keyWord: keyword,
    level: 12,
    mapBound: "73.66,3.86,135.05,53.55",
    queryType: 7,
    start: 0,
    count: 20,
  });
  const url = `https://api.tianditu.gov.cn/v2/search?postStr=${encodeURIComponent(postStr)}&type=query&tk=${TIANDITU_TOKEN}`;
  try {
    const resp = await fetch(url);
    if (!resp.ok) return [];
    const data = await resp.json();
    if (data && data.pois) {
      return data.pois.map((poi: any) => ({
        value: poi.name,
        name: poi.name,
        district: poi.address || "",
        location: poi.lonlat
          ? {
              lng: Number(poi.lonlat.split(" ")[0]),
              lat: Number(poi.lonlat.split(" ")[1]),
            }
          : null,
        adcode: poi.admincode || "",
        _raw: poi,
      }));
    }
    return [];
  } catch {
    return [];
  }
}

async function tdtReverseGeocode(lng: number, lat: number): Promise<string> {
  const postStr = JSON.stringify({ lon: lng, lat, ver: 1 });
  const url = `https://api.tianditu.gov.cn/geocoder?postStr=${encodeURIComponent(postStr)}&type=geocode&tk=${TIANDITU_TOKEN}`;
  const resp = await fetch(url);
  const data = await resp.json();
  if (data && data.result) {
    return data.result.formatted_address || data.result.address || "";
  }
  return "";
}

// ==================== 响应式状态 ====================
let map: any = null;
let marker: any = null;
let infoWindow: any = null;

const dialogVisible = ref(false);
const keyword = ref("");
const model = ref<any>([]);

const refMap = ref<HTMLDivElement | null>(null);
const center = ref<[number, number]>([116.397428, 39.90923]);
const zoom = ref(10);

// ==================== 标记管理 ====================
function clearMarker() {
  if (marker) {
    map?.removeOverLay(marker);
    marker = null;
  }
}

async function addMarker(lnglat: [number, number], name?: string) {
  const T = getT();
  if (!T || !map) return;

  clearMarker();

  const icon = new T.Icon({
    iconUrl: markerIcon,
    iconSize: new T.Point(19, 33),
    iconAnchor: new T.Point(10, 33),
  });

  marker = new T.Marker(new T.LngLat(lnglat[0], lnglat[1]), { icon });
  map.addOverLay(marker);

  if (name) {
    keyword.value = name;
    model.value = [lnglat[0], lnglat[1], name];
    return;
  }

  try {
    const address = await tdtReverseGeocode(lnglat[0], lnglat[1]);
    keyword.value =
      address || `${lnglat[0].toFixed(6)}, ${lnglat[1].toFixed(6)}`;
    model.value = [lnglat[0], lnglat[1], keyword.value];
  } catch {
    model.value = [
      lnglat[0],
      lnglat[1],
      `${lnglat[0].toFixed(6)}, ${lnglat[1].toFixed(6)}`,
    ];
  }
}

// ==================== 地图初始化 ====================
async function initMap() {
  await loadTiandituScript();
  const T = getT();
  if (!T) return;

  const container = refMap.value;
  if (!container) return;

  if (map) {
    try {
      map.destroy();
    } catch {
      /* ignore */
    }
    map = null;
    marker = null;
  }

  const hasInitPos = model.value?.[0] != null && model.value?.[1] != null;
  const mapCenter = hasInitPos
    ? new T.LngLat(model.value[0], model.value[1])
    : new T.LngLat(center.value[0], center.value[1]);
  const mapZoom = hasInitPos ? options.value?.zoom || 15 : zoom.value;

  map = new T.Map(container, {
    center: mapCenter,
    zoom: mapZoom,
  });

  map.addControl(new T.Control.Zoom());

  // 地图点击 → 选点（仅编辑态）
  // view 模式是只读展示，点击地图不得改动标点位置（addMarker 会先 clearMarker 再重新打点，
  // 还会触发逆地理编码请求），因此这里不绑定该监听。
  if (props.type !== "view") {
    map.addEventListener("click", (e: any) => {
      const lnglat = e.lnglat;
      if (!lnglat) return;
      addMarker([lnglat.getLng(), lnglat.getLat()]);
    });
  }

  if (hasInitPos) {
    await nextTick();
    addMarker([model.value[0], model.value[1]], model.value[2]);
  }
}

function destroyMap() {
  clearMarker();
  if (infoWindow) {
    try {
      infoWindow.closeInfoWindow();
    } catch {
      /* ignore */
    }
    infoWindow = null;
  }
  if (map) {
    try {
      map.destroy();
    } catch {
      /* ignore */
    }
    map = null;
  }
}

// ==================== 打开选择器 ====================
const handleOpenDialog = async () => {
  if (disabled.value) return;
  dialogVisible.value = true;
  await nextTick();
  model.value = JSON.parse(JSON.stringify(modelValue.value));
  setTimeout(() => initMap(), 200);
};

// ==================== 自动补全搜索 ====================
const LAST_SEARCH_RESULTS = ref<any[]>([]);

const querySearch = async (queryString: string, cb: any) => {
  if (!queryString) return cb([]);
  try {
    const results = await tdtSearch(queryString);
    LAST_SEARCH_RESULTS.value = results;
    cb(results);
  } catch {
    ElMessage.error("搜索失败，请稍后重试");
    cb([]);
  }
};

const inputChange = () => {
  model.value = [];
  clearMarker();
};

// ==================== 选中搜索结果 ====================
/**
 * 从 item 中提取坐标（优先 item.location，其次 item._raw.lonlat）
 */
function extractCoords(item: any): [number, number] | null {
  if (item.location && !isNaN(item.location.lng) && !isNaN(item.location.lat)) {
    return [item.location.lng, item.location.lat];
  }
  if (item._raw?.lonlat) {
    const [lng, lat] = item._raw.lonlat.split(",").map(Number);
    if (!isNaN(lng) && !isNaN(lat)) return [lng, lat];
  }
  return null;
}

/**
 * 定位到指定坐标并添加标记
 */
function locateTo(lngLat: [number, number], name: string) {
  addMarker(lngLat, name);
  map?.panTo(new (getT().LngLat)(lngLat[0], lngLat[1]));
}

const handleSelect = async (item: any) => {
  console.log("🚀 ~ item:", item);
  keyword.value = item.name;

  // 1) item 本身有坐标 → 直接定位打点
  const coords = extractCoords(item);
  console.log("🚀 ~ coords:", coords);
  if (coords) {
    locateTo(coords, item.name);
    return;
  }

  // 2) 无坐标 → 远程搜索后定位
  try {
    const results = await tdtSearch(item.name);
    const first = results[0];
    if (first) {
      const firstCoords = extractCoords(first);
      if (firstCoords) {
        locateTo(firstCoords, first.name);
        return;
      }
    }
    ElMessage.warning("未找到匹配的地点");
  } catch {
    ElMessage.warning("搜索失败，请稍后重试");
  }
};

// ==================== Enter 键搜索 ====================
const handleEnterKey = () => {
  if (!keyword.value) return;
  if (LAST_SEARCH_RESULTS.value.length > 0) {
    handleSelect(LAST_SEARCH_RESULTS.value[0]);
  } else {
    handleSelect({ value: keyword.value, name: keyword.value });
  }
};

// ==================== 确认 / 清除 ====================
const handleConfirm = () => {
  emits("update:modelValue", model.value);
  emits("change", model.value);
  emits("update:addressCode", [model.value[0], model.value[1]]);
  emits("update:address", model.value[2]);
  dialogVisible.value = false;
  setTimeout(() => destroyMap(), 300);
};

const clearValue = () => {
  emits("update:modelValue", []);
  emits("change", []);
};

// ==================== 地图中心更新 ====================
function updateMapCenter(val: any[], retry = 0) {
  const T = getT();
  if (!map || !T) {
    if (retry < 10) {
      setTimeout(() => updateMapCenter(val, retry + 1), 300);
    }
    return;
  }
  addMarker([val[0], val[1]], val[2]);
  map.panTo(new T.LngLat(val[0], val[1]), options.value?.zoom || 12);
}

// ==================== View 模式：挂载时初始化 ====================
onMounted(() => {
  if (props.type === "view") {
    nextTick(async () => {
      model.value = JSON.parse(JSON.stringify(modelValue.value));
      await new Promise((r) => requestAnimationFrame(r));
      try {
        await initMap();
      } catch (e) {
        console.error("[ViewMap] initMap error:", e);
      }
    });
  }
});

onUnmounted(() => {
  destroyMap();
});

// ==================== 监听 modelValue 外部变化 ====================
watch(
  () => props.modelValue,
  (val) => {
    if (!val || val[0] == null || val[1] == null) return;
    model.value = JSON.parse(JSON.stringify(val));
    updateMapCenter(val);
  },
  { deep: true },
);

defineExpose({
  handleOpenDialog,
});
</script>

<template>
  <!-- type="view"：只读地图 -->
  <div
    v-if="type === 'view'"
    class="i-map w-full h-full i-map__container-inner"
  >
    <div ref="refMap" class="i-map__tdt i-map__tdt--view" />
  </div>

  <!-- type="edit" / "edit2"：输入框 + 抽屉 / 纯抽屉 -->
  <div v-else class="i-map">
    <!-- edit 模式：输入框触发器 -->
    <el-input
      v-if="type === 'edit'"
      v-model="modelValue[2]"
      class="i-map__input"
      :placeholder="placeholder"
      readonly
      :size="size"
      :disabled="disabled"
      @click="handleOpenDialog"
    >
      <template v-if="!disabled" #suffix>
        <el-icon
          v-if="modelValue[2]"
          class="i-map__input-close"
          @click.stop="clearValue"
        >
          <CircleClose />
        </el-icon>
      </template>
    </el-input>

    <!-- 共享抽屉 -->
    <el-drawer
      v-model="dialogVisible"
      title="请选择地点"
      :show-close="true"
      size="100%"
      @closed="destroyMap"
    >
      <div
        v-if="dialogVisible"
        class="h-full w-full overflow-hidden i-map__container"
      >
        <el-autocomplete
          v-model="keyword"
          :disabled="disabled"
          placeholder="请输入关键字选择地点"
          class="i-map__search"
          :debounce="700"
          style="margin-bottom: 15px; width: 100%"
          clearable
          :fetch-suggestions="querySearch"
          :validate-event="false"
          @input="inputChange"
          @select="handleSelect"
          @keyup.enter="handleEnterKey"
        >
          <template #default="{ item }">
            {{ item.name }}
            <span class="i-map__district">{{ item.district }}</span>
          </template>
        </el-autocomplete>
        <div class="i-map__container-inner">
          <div ref="refMap" class="i-map__tdt" />
        </div>
      </div>
      <template #footer>
        <el-button type="primary" :disabled="disabled" @click="handleConfirm">
          确定
        </el-button>
      </template>
    </el-drawer>
  </div>
</template>
